/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import { Campaign, withCampaigns } from '@domain/campaigns';
import { Table } from '@paretogroup/tess';
import { compose } from '@shared/redux';
import { TableHeaderComponent } from '@shared/tables/components/table-header/table-header-component';
import {
    getFilteredData,
    getMetricsByContentType,
    PROJECTION_METRICS,
    METRICS_WITH_PROJECTION,
} from '@shared/tables';
import { isDataLoading } from '@shared/axios';
import LoadingSkeleton from '@shared/components/loading/LoadingSkeleton';
import { ALL_METRICS } from '@domain/campaigns/campaign-table/campaign-table-metrics.constants';
import { ObjectSelected, withObjectSelected } from '@domain/object-selected';
import { DEFAULT_SORT } from '@domain/campaigns/campaign-table';
import { isBudgetManagerTagGroup } from '@domain/budget-manager/budget-manager.utils';
import { withSegments } from '../segments.hocs';
import { Segments, SelectedGroupWithSegments } from '../segments.types';
import {
    formatSegmentsToColumns, sortMetricsColumnsByIndex,
} from './segments-table.utils';
import { SEGMENTS_TABLE_APPCUES_ID, SEGMENTS_TABLE_HEADER_TEXTS } from './segments-table-header/segments-table-header.constants';

interface CampaignsState {
    data: Campaign[];
    loadState: string;
}

interface TagGroupsState {
    loadState: string;
    data: Segments[];
    selectedGroup: SelectedGroupWithSegments;
}

interface SegmentsTableProps {
    campaigns: CampaignsState;
    tagGroups: TagGroupsState;
    objectSelected: ObjectSelected;
    data: any;
    fixedColumns: any[];
    tableHeaderText: any;
    classes: Record<any, any>;
    filter: string;
    setFilter: Function;
    selectedMetrics: string[];
    setSelectedMetrics: Function
}

const SegmentsTableImpl = ({
    campaigns,
    tagGroups,
    objectSelected,
    data,
    fixedColumns,
    tableHeaderText = SEGMENTS_TABLE_HEADER_TEXTS,
    classes,
    filter,
    setFilter,
    selectedMetrics,
    setSelectedMetrics,
}: SegmentsTableProps) => {
    const onChange = ({ target }) => {
        const { value } = target;
        const valueLowerCase = value.toLowerCase();
        setFilter(valueLowerCase);
    };

    const isLoading = isDataLoading(campaigns.loadState) || isDataLoading(tagGroups.loadState);
    const dataMemo = useMemo(() => (filter
        ? getFilteredData(data, filter)
        : data), [filter, data]);

    const metricsFilteredByName = selectedMetrics.map(
        metric => ALL_METRICS[metric],
    );

    const sortedMetricsColumns = sortMetricsColumnsByIndex(metricsFilteredByName);

    const columns = useMemo(() => formatSegmentsToColumns([
        ...fixedColumns,
        ...sortedMetricsColumns,
    ]), [fixedColumns, metricsFilteredByName]);

    const segmentsMetrics = getMetricsByContentType(objectSelected, METRICS_WITH_PROJECTION);

    const isOBMTable = isBudgetManagerTagGroup(tagGroups.selectedGroup.slug);

    return (
        <>
            <TableHeaderComponent
                onChange={onChange}
                searchText={filter}
                selectedMetrics={selectedMetrics}
                setSelectedMetrics={setSelectedMetrics}
                metrics={segmentsMetrics}
                advancedMetrics={PROJECTION_METRICS}
                texts={tableHeaderText}
                dataAppcuesId={SEGMENTS_TABLE_APPCUES_ID.INSIGHT_HEADER}
                isOBMTable={isOBMTable}
            />
            { isLoading
                ? <LoadingSkeleton count={11} height="58px" />
                : (
                    <Table
                        data={dataMemo}
                        columns={columns}
                        classes={classes}
                        displayTotal
                        displayPagination={dataMemo?.length > 10}
                        defaultSortBy={DEFAULT_SORT}
                    />
                )
            }

        </>
    );
};

export const SegmentsTable = compose(
    withSegments,
    withCampaigns,
    withObjectSelected,
)(SegmentsTableImpl);
